import type { MenuProps } from 'antd'
import { Menu } from 'antd'
import React, { useMemo } from 'react'
import { Link } from 'react-router'
import { MenuDataProps } from '@/common/types/components.def.ts'
import { useAuth } from '@/common/hooks/useAuth.ts'
import { intersection } from 'lodash-es'
import { ModuleFunction } from '@/features/auth/auth.def.ts'
import { useEmployeePublicInfo } from '@/common/hooks/useEmployeePublicInfo.tsx'
import { getNavMenuData } from '@/common/layouts/NavMenuData.tsx'

type MenuItem = Required<MenuProps>['items'][number];

type MenuVisibilityUserContext = {
  employeeId: string
  userRoles: string[]
  isHc: boolean
  checkHasAllPermissions: (x: ModuleFunction[]) => boolean
  checkIsManager: (x: string) => boolean
}

const getMenuItemVisibility = ({
  menuDataProps,
  userContext,
}: {
  menuDataProps: MenuDataProps
  userContext: MenuVisibilityUserContext
}): boolean => {
  let access = true

  if (menuDataProps.allowRoles?.length && !intersection(menuDataProps.allowRoles, userContext.userRoles).length) {
    access = false
  }

  if (menuDataProps.requiredPermissions?.length && !userContext.checkHasAllPermissions(menuDataProps.requiredPermissions)) {
    access = false
  }

  if (menuDataProps.managerOnly && !userContext.checkIsManager(userContext.employeeId) && !userContext.isHc) {
    return false
  }

  return access
}

type MenuDataToItemsArgs = {
  parentKey?: string
  children?: MenuDataProps[]
}

const menuDataToItems = (args: MenuDataToItemsArgs, userContext: MenuVisibilityUserContext): MenuItem[] => {
  return (args.children || []).filter(x => getMenuItemVisibility({
    menuDataProps: x,
    userContext: userContext,
  })).map(
    x => ({
      key: args.parentKey ? `${args.parentKey}.${x.key}` : x.key,
      label: x.url ? <Link to={x.url} style={{ color: 'inherit' }} target={x.target}>{x.label}</Link> : x.label,
      icon: x.icon,
      children: x.children?.length ? menuDataToItems(
        { parentKey: x.key, children: x.children },
        userContext,
      ) : undefined,
    }))
}

export const NavMenu: React.FC<{ onItemClick?: (key: string) => void }> = ({ onItemClick }) => {
  const { user, isHc, checkHasAllPermissions, userProfile } = useAuth()
  const { checkIsManager } = useEmployeePublicInfo()

  const navItems = useMemo(() => {
    return menuDataToItems(
      {
        parentKey: '',
        children: getNavMenuData(userProfile?.employeeId),
      },
      {
        employeeId: userProfile?.employeeId || '',
        userRoles: user?.roles || [],
        isHc,
        checkHasAllPermissions,
        checkIsManager,
      },
    )
  }, [checkIsManager, checkHasAllPermissions, user?.roles, userProfile?.employeeId])

  return <Menu selectable={false} items={navItems} mode="inline" onClick={({ key }) => onItemClick?.(key)} />
}